import {appUrl} from '@frontend/AppUrl'
import {trans} from '@frontend/helpers'
import {useRegionConfig} from '@frontend/hooks'
import {useRecaptcha} from '@frontend/hooks/useRecaptcha'
import {LoginFormStep1} from '@frontend/modules/public/auth/components/LoginFormStep1'
import {LoginFormStep2} from '@frontend/modules/public/auth/components/LoginFormStep2'
import {
    BehaviorStepName,
    ClientBehaviorService,
} from '@frontend/services/ClientBehaviorService'
import {
    useAuthStore,
    useLocaleStore,
} from '@frontend/stores'
import type {RegionConfig} from '@frontend/types/Locales'
import type {UserData} from '@frontend/types/User'
import {Recaptcha3Service} from '@shared/services/Recaptcha3Service'
import clsx from 'clsx'
import {
    useEffect,
    useEffectEvent,
    useState,
} from 'react'
import {Collapse} from 'swayok-react-mdb-ui-kit/components/Collapse/Collapse'
import {normalizeAndCleanMaskedValue} from 'swayok-react-mdb-ui-kit/components/Input/MaskedNumericInput'
import {Loading} from 'swayok-react-mdb-ui-kit/components/Loading/Loading'
import {
    handleErrorResponse,
    handleHttp4xxErrorResponse,
    ValidationErrorHttpCode,
} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import {AnalyticsEventReporter} from '../../../../helpers/AnalyticsEventReporter'
import {UserAuthApi} from '../UserAuthApi'

interface Props {
    inModal?: boolean
    registration?: boolean
    disableDefaultOnSuccess?: boolean
    phone?: string | null
    onSuccess?: (user: UserData) => void
}

// Форма авторизации.
// 2 этапа: ввод номера телефона и ввод проверочного кода из SMS.
export function LoginForm(props: Props) {

    const {
        inModal,
        registration,
        disableDefaultOnSuccess = false,
        phone: propsPhone,
        onSuccess,
    } = props

    const user = useAuthStore(
        state => state.user
    )
    const setUserData = useAuthStore(
        state => state.setUserData
    )
    const finishUserAuth = useAuthStore(
        state => state.finishUserAuth
    )
    const regionConfig = useRegionConfig()
    const phoneConfig: RegionConfig['phone'] = regionConfig.phone
    const verificationCodeConfig: RegionConfig['verificationCode'] = regionConfig.verificationCode
    const applyPreferredUserLanguage = useLocaleStore(
        state => state.applyPreferredUserLanguage
    )

    // На каком шаге авторизации находимся.
    const [
        isVerificationStep,
        setIsVerificationStep,
    ] = useState<boolean>(false)
    // Вводимые данные.
    const [
        phone,
        setPhone,
    ] = useState<string>(() => normalizePhoneNumber(propsPhone, regionConfig))
    const [
        verificationCode,
        setVerificationCode,
    ] = useState<string>('')
    // Повторная отправка кода проверки.
    const [
        resendingTimeout,
        setResendingTimeout,
    ] = useState<number>(0)
    const [
        resendsCount,
        setResendsCount,
    ] = useState<number>(0)
    // Ошибки.
    const [
        phoneError,
        setPhoneError,
    ] = useState<string>('')
    const [
        verificationCodeError,
        setVerificationCodeError,
    ] = useState<string>('')

    // Задан ли номер телефона в props?
    const hasPhoneInProps: boolean = (
        !!propsPhone
        && phone.length > 0
        && phoneConfig.validationRegexp.test(phone)
    )

    // Состояние отправки данных на сервер.
    const [
        isSubmitting,
        setIsSubmitting,
    ] = useState<boolean>(hasPhoneInProps)
    // Состояние полной блокировки функционала формы.
    const [
        isBlocked,
        setIsBlocked,
    ] = useState<boolean>(false)

    // Состояние доступности интерактивных элементов формы.
    const isDisabled: boolean = isSubmitting || isBlocked

    // Инициализация Recaptcha.
    useRecaptcha()

    // Трекинг поведения.
    useEffect(() => {
        ClientBehaviorService.startStep(BehaviorStepName.LOGIN)
    }, [])

    // Обработка ошибочного ответа на запрос SMS-кода.
    const onPhoneSubmitError = useEventCallback((
        error: ApiError
    ): void => {
        if (error.status === 404) {
            // Пользователь не зарегистрирован - направляем его на страницу
            // регистрации без отправки SMS-кода (это будет дальше).
            NavigationService.navigate(
                appUrl().registration,
                null,
                {phone}
            )
            return
        }

        handleErrorResponse(error, {
            suppressErrorMessagesForHttpCodes: [428],
            validationErrors(errors, response) {
                // Кроме телефона там нечего больше валидировать.
                setPhoneError(
                    errors.phone
                    ?? response.data._message
                    ?? trans().http_error.code422.toast
                )
            },
            http4xxError(response, handlers) {
                switch (response.status) {
                    case 423:
                    case 429:
                        // Блокировка пользователя или отправки SMS-кодов.
                        // Блокируем все интерактивные элементы формы.
                        setIsBlocked(true)
                        setPhoneError(
                            response.data._message ?? trans().http_error.code4xx.toast
                        )
                        return
                    case 428:
                        // Подозрение на спам.
                        // Блокируем все интерактивные элементы формы.
                        setIsBlocked(true)
                        setPhoneError(
                            trans().auth.verification_code.recaptcha_challenge_failed
                        )
                        return
                    case 412:
                        // Провал проверки на спам.
                        // Блокируем все интерактивные элементы формы.
                        setIsBlocked(true)
                        setPhoneError(
                            response.data._message
                            ?? trans().auth.verification_code.recaptcha_challenge_failed
                        )
                        return
                    default:
                        // Другие ошибки, включая валидацию.
                        handleHttp4xxErrorResponse(response, handlers)
                }
            },
            http5xxError() {
                setPhoneError(trans().http_error.unknown.toast)
            },
        })

        AnalyticsEventReporter.loginFormSmsCodeSendingResult(
            false,
            error.status,
            error.data?._message ?? '[no message]',
            error.data ?? {}
        )

        setIsSubmitting(false)
    })

    // Проверка номера телефона и отправка запроса на отправку проверочного SMS-кода.
    const onPhoneSubmit = useEventCallback((
        event?: Event | null,
        ignoreDisabledState: boolean = false,
        abortController?: AbortController
    ): void => {
        event?.preventDefault()
        if (!ignoreDisabledState && isDisabled) {
            return
        }
        if (!phoneConfig.validationRegexp.test(phone)) {
            AnalyticsEventReporter.loginFormSmsCodeSendingResult(
                false,
                ValidationErrorHttpCode,
                'Frontend validation: Invalid phone format',
                {
                    phone,
                    validationRegexp: phoneConfig.validationRegexp.toString(),
                }
            )
            setPhoneError(trans().auth.login.invalid_phone_format)
            return
        }
        setIsSubmitting(true)
        // Запускаем проверку Recaptcha v3.
        Recaptcha3Service.runChallenge('login')
            .then((recaptchaToken: string | null): void => {
                if (!recaptchaToken) {
                    // Не можем выполнить проверку.
                    setIsBlocked(true)
                    setIsSubmitting(false)
                    setPhoneError(trans().auth.verification_code.recaptcha_challenge_failed)
                    AnalyticsEventReporter.loginFormSmsCodeSendingResult(
                        false,
                        500,
                        'Frontend validation: Recaptcha challenge returned empty token'
                    )
                    return
                }
                const request = registration
                    ? UserAuthApi.requestPhoneVerificationCode(phone, recaptchaToken, abortController)
                    : UserAuthApi.resetPassword(phone, recaptchaToken, abortController)

                request
                    .then(data => {
                        setIsVerificationStep(true)
                        setIsSubmitting(false)
                        setResendingTimeout(data.resend_timeout ?? verificationCodeConfig.resendTimeout)
                        setResendsCount(resendsCount + 1)
                        AnalyticsEventReporter.loginFormSmsCodeSendingResult(
                            true,
                            undefined,
                            undefined,
                            data
                        )
                    })
                    .catch((error: ApiError) => onPhoneSubmitError(error))
            })
            .catch((error: unknown) => {
                console.error('ReCaptcha failed', error)
                setIsBlocked(true)
                setIsSubmitting(false)
                setPhoneError(trans().auth.verification_code.recaptcha_challenge_failed)
                AnalyticsEventReporter.loginFormSmsCodeSendingResult(
                    false,
                    500,
                    'Frontend validation: Recaptcha challenge failed'
                )
                AnalyticsEventReporter.exception(error, 'LoginForm -> Recaptcha challenge')
            })
    })

    // Обработка успешного входа в ЛК.
    const onLoginSuccess = useEventCallback((
        userData: UserData
    ): void => {
        // Отправляем событие в сервисы аналитики.
        AnalyticsEventReporter.loginSuccess(userData)

        if (!disableDefaultOnSuccess) {
            if (registration) {
                // Форма находится на страницах регистрации.
                setUserData(userData, true)
                // Сохраняем данные об устройстве пользователя.
                // void DeviceInfoTrackerService.trackDeviceInfo(
                //     'login',
                //     userData.details.id,
                //     userData.phone,
                //     languageConfig.full
                // )
                if (user?.registeredAt) {
                    // Регистрация уже завершена - перенаправляем в кабинет.
                    NavigationService.navigate(appUrl().cabinet.default)
                }
                // setIsSubmitting(false) - не нужен! Очистка полей ввода - тоже.
            } else {
                // Форма находится в модальном окне или на странице входа в ЛК.
                finishUserAuth(userData, !userData.registeredAt)
                if (!userData.registeredAt) {
                    // Пользователь не до конца зарегистрирован,
                    // нужно перенаправить его на страницу регистрации.
                    NavigationService.navigate(appUrl().registration, null)
                } else {
                    // Применяем предпочтительный для пользователя язык интерфейса.
                    applyPreferredUserLanguage(userData.preferredLanguage)
                }
            }
        }
        ClientBehaviorService.finishStep()
        onSuccess?.(userData)
        // setIsSubmitting(false) - не нужен! Очистка полей ввода - тоже.
    })

    // Проверка введенного проверочного кода и отправка запроса на авторизацию.
    const onCodeSubmit = useEventCallback((): void => {
        if (isDisabled) {
            return
        }
        if (
            !verificationCodeConfig.validationRegexp.test(verificationCode)
            || verificationCode.length !== verificationCodeConfig.digits
        ) {
            setVerificationCodeError(trans().auth.verification_code.invalid_code)
            return
        }
        setIsSubmitting(true)
        UserAuthApi.login(phone, verificationCode)
            .then(onLoginSuccess)
            .catch((error: ApiError) => {
                setIsSubmitting(false)
                handleErrorResponse(error, {
                    validationErrors(errors, response) {
                        // Кроме кода там нечего больше валидировать.
                        setVerificationCodeError(
                            errors.code
                            ?? response.data._message
                            ?? trans().http_error.code422.toast
                        )
                    },
                    http4xxError(response, handlers) {
                        switch (response.status) {
                            case 423:
                                // Блокировка пользователя.
                                // Блокируем все интерактивные элементы формы.
                                setIsBlocked(true)
                                setVerificationCodeError(
                                    response.data._message ?? trans().http_error.code4xx.toast
                                )
                                return
                            default:
                                // Другие ошибки, включая валидацию.
                                handleHttp4xxErrorResponse(response, handlers)
                        }
                    },
                    http5xxError() {
                        setVerificationCodeError(trans().http_error.unknown.toast)
                    },
                })
            })
    })

    // Если props.phone задан и валиден, то отправляем запрос на отправку SMS-кода.
    const submitPhoneFromProps = useEffectEvent((
        abortController: AbortController
    ) => {
        if (hasPhoneInProps) {
            onPhoneSubmit(null, true, abortController)
        }
    })

    // При монтировании обрабатываем ситуацию, когда props.phone задан.
    useEffect(() => {
        const abortController: AbortController = new AbortController()
        submitPhoneFromProps(abortController)
        return () => {
            abortController.abort()
        }
    }, [])

    let formClassName: string = 'container-sm pt-7 pb-4'
    if (inModal) {
        formClassName = 'in-modal pt-4 pb-4'
    } else if (registration) {
        formClassName = 'container-sm'
    }

    return (
        <div
            id="login-form"
            className={clsx('text-center', formClassName)}
        >
            <Collapse
                show={!isVerificationStep && !isSubmitting}
                showImmediately={!hasPhoneInProps}
            >
                <LoginFormStep1
                    phone={phone}
                    phoneConfig={phoneConfig}
                    phoneError={phoneError}
                    disabled={isDisabled}
                    setPhone={(phone: string) => {
                        setPhone(phone)
                        setPhoneError('')
                    }}
                    onSubmit={onPhoneSubmit}
                    registration={registration}
                />
            </Collapse>
            <Collapse
                show={!isVerificationStep && isSubmitting}
                showImmediately={hasPhoneInProps}
            >
                <div className="flex-center py-5">
                    <Loading
                        loading={true}
                        label={trans().auth.verification_code.sending}
                    />
                </div>
            </Collapse>
            <Collapse show={isVerificationStep}>
                <LoginFormStep2
                    visible={isVerificationStep}
                    phone={phone}
                    verificationCode={verificationCode}
                    verificationCodeError={verificationCodeError}
                    resendingTimeout={resendingTimeout}
                    resend={resendsCount}
                    disabled={isDisabled}
                    setVerificationCode={(code: string) => {
                        setVerificationCode(code)
                        setVerificationCodeError('')
                    }}
                    onSubmit={onCodeSubmit}
                    onResendCode={onPhoneSubmit}
                />
            </Collapse>
        </div>
    )
}

// Проверить номер телефона и отформатировать его для PhoneInput.
function normalizePhoneNumber(
    phone: string | undefined | null,
    regionConfig: RegionConfig
): string {
    if (!phone || phone.trim().length === 0) {
        return ''
    }
    phone = normalizeAndCleanMaskedValue(phone, regionConfig.phone.template)
    return regionConfig.phone.validationRegexp.test(phone) ? phone : ''
}
